import React, { useRef } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Button, Input } from 'tea-component';
import ImageUploader, { IImageUploaderRef } from '../ImageUploader';
import { DBI_IMG_COS_PATH } from '@user-center/_config';

interface ICollaboratorEditorProps {
  authorNumber: number;
  value: IReportCollaboratorFormModel;
  submitted: boolean;
  canAdd: boolean;
  loading?: boolean;
  isOpenIdDuplicate?: boolean;
  onChange: (patch: Partial<IReportCollaboratorFormModel>) => void;
  onAdd: () => void;
  onDelete: () => void;
  onOpenIdChange?: (openId: string) => void;
  onAvatarUpload?: (file: File) => void;
}

const CollaboratorEditor: React.FC<ICollaboratorEditorProps> = ({
  authorNumber,
  value,
  submitted,
  canAdd,
  loading,
  isOpenIdDuplicate,
  onChange,
  onAdd,
  onDelete,
  onOpenIdChange,
  onAvatarUpload,
}) => {
  const { formatMessage } = useIntl();
  const openIdInvalid = submitted && (!value.open_id || !!isOpenIdDuplicate);
  const departmentInvalid = submitted && !value.creator_department_en;
  const imageUploaderRef = useRef<IImageUploaderRef | null>(null);

  return (
    <div
      className={classnames('dbu-report-collaborator-editor', {
        'has-error': submitted && (!value.open_id || !value.creator_department_en || !!isOpenIdDuplicate),
      })}
    >
      <span className="dbu-report-collaborator-editor__label">
        {formatMessage({ id: 'dbu_report_author_number' }, { number: authorNumber })}
      </span>
      <ImageUploader
        ref={imageUploaderRef}
        className="dbu-report-collaborator-editor__avatar"
        loading={loading}
        url={value.creator_avatar?.url || ''}
        enableDropUpload
        showPreviewUploadTrigger
        hideUploadButton
        buttonLabel={formatMessage({ id: 'dbu_report_update_image' })}
        onUpload={(file, url) => {
          onChange({
            creator_avatar: {
              key: `${DBI_IMG_COS_PATH}/report-avatar-${file.name}`,
              url,
              file_name: file.name,
              file,
            },
          });
          onAvatarUpload?.(file);
        }}
        onClear={() => onChange({ creator_avatar: null })}
        onImageLoadError={() => onChange({ creator_avatar: null })}
      />
      <div className="dbu-report-collaborator-editor__info">
        <Input
          className={classnames('dbu-report-collaborator-editor__open-id', { 'is-error': openIdInvalid })}
          value={value.open_id}
          placeholder={formatMessage({ id: 'dbu_report_author_placeholder' })}
          onChange={(openId) => {
            onChange({ open_id: openId, creator_name_en: openId });
            onOpenIdChange?.(openId);
          }}
        />
        <Input
          className={classnames('dbu-report-collaborator-editor__department', { 'is-error': departmentInvalid })}
          value={value.creator_department_en || ''}
          placeholder={formatMessage({ id: 'dbu_report_author_org_structure_placeholder' })}
          onChange={(department) => onChange({ creator_department: department, creator_department_en: department })}
        />
        <div className="dbu-report-collaborator-editor__actions">
          <Button htmlType="button" onClick={() => imageUploaderRef.current?.open()}>
            {formatMessage({ id: 'dbu_report_update_image' })}
          </Button>
          <div className="dbu-report-collaborator-editor__author-actions">
            <Button type="link" htmlType="button" onClick={onDelete}>
              {formatMessage({ id: 'dbu_report_delete_author' })}
            </Button>
            {canAdd && (
              <Button type="link" htmlType="button" onClick={onAdd}>
                {formatMessage({ id: 'dbu_report_add_author' })}
              </Button>
            )}
          </div>
        </div>
        {submitted && (!value.open_id || !value.creator_department_en || !!isOpenIdDuplicate) && (
          <div className="dbu-report-collaborator-editor__error">
            {formatMessage({
              id: (!value.open_id || !value.creator_department_en)
                ? 'dbu_report_author_required_tips'
                : 'dbu_report_author_duplicate_tips',
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaboratorEditor;
